import type { TFunction } from 'i18next';
import { localeTag } from '@/lib/localeTag';
import type { LoyaltyProgram, RewardType } from './loyaltyApi';

// Shared by the loyalty page and the checkout loyalty step (SC-1091).

interface Valued {
  type: RewardType;
  value: string | null;
}

/** `K7M2QX9PAB` → `K7M2-QX9P-AB`: a code read aloud or retyped is grouped by four. */
export function formatCode(code: string): string {
  return code.replace(/(.{4})(?=.)/g, '$1-');
}

/**
 * What the customer typed or pasted → what the server compares. Same rule as
 * the hub's `normalize_code`: uppercase, no spaces, no grouping dashes, and the
 * `OTL:` prefix a QR carries. Legacy 16-hex codes go through unchanged.
 */
export function normalizeRedemptionCode(raw: string): string {
  return raw
    .toUpperCase()
    .replace(/[\s-]/g, '')
    .replace(/^OTL:/, '');
}

// Backend sends `value` as a decimal string (e.g. "5.00"). Drop the trailing
// zeros for whole numbers, otherwise render with a comma per the app's
// French-locale number convention (matches Price.tsx's `12,90 €` style).
export function formatNumber(raw: string): string {
  const n = Number(raw);
  if (Number.isNaN(n)) return raw;
  return Number.isInteger(n) ? String(n) : n.toFixed(2).replace('.', ',');
}

export function rewardValueLabel(t: TFunction, reward: Valued): string | null {
  if (reward.value == null) return null;
  const value = formatNumber(reward.value);
  if (reward.type === 'percent') return t('loyalty.valuePercent', { value });
  if (reward.type === 'fixed') return t('loyalty.valueFixed', { value });
  return null;
}

function positiveValue(reward: Valued): number | null {
  if (reward.value == null) return null;
  const n = Number(reward.value);
  return Number.isFinite(n) && n > 0 ? n : null;
}

/**
 * Checkout can only apply a `fixed` / `percent` reward with a non-zero value.
 * The server says so with `usable_online`; the fallback mirrors its rule so a
 * hub that predates the flag still shows the same options.
 */
export function isUsableOnline(reward: Valued & { usable_online?: boolean }): boolean {
  if (typeof reward.usable_online === 'boolean') return reward.usable_online;
  return (reward.type === 'fixed' || reward.type === 'percent') && positiveValue(reward) !== null;
}

/**
 * The loyalty discount sale_api will apply, in integer cents — EXACT, not an
 * estimate: `views_online.py` computes `min(fixed, goods)` or
 * `min(round_half_up(goods × pct / 100), goods)`. `goodsCents` is the goods AFTER
 * the order promotion (`subtotal − promo`); delivery and cart options are never
 * discounted and never part of the base.
 *
 * The percent is done in integers so the half-up matches Decimal's, with no float
 * drift: pctBasis = 1500 for « 15.00 », then floor((goods × 1500 + 5000) / 10000).
 * (1550 at 15 % = 2.325 → 233 ; 1562 → 234.3 → 234.)
 */
export function redemptionDiscountCents(reward: Valued, goodsCents: number): number {
  const value = positiveValue(reward);
  if (value === null || goodsCents <= 0) return 0;
  if (reward.type === 'fixed') return Math.min(Math.round(value * 100), goodsCents);
  if (reward.type === 'percent') {
    const pctBasis = Math.round(value * 100);
    return Math.min(Math.floor((goodsCents * pctBasis + 5000) / 10000), goodsCents);
  }
  return 0;
}

/** Stripe refuses a card payment under 0,50 €. */
export const ONLINE_MIN_CENTS = 50;

/**
 * True when an applied advantage (`redemptionCents` > 0) leaves less than the
 * online minimum to pay. sale_api refuses such an ONLINE order up front
 * (`online_amount_too_small`); the client must not offer it. A tiny cart with no
 * advantage is not this case and keeps its usual behaviour.
 */
export function leavesTooLittleOnline(finalTotalCents: number, redemptionCents: number): boolean {
  return redemptionCents > 0 && finalTotalCents < ONLINE_MIN_CENTS;
}

/**
 * The order total once the advantage is applied: the total the page already
 * computes (goods − promo + delivery + cart options) minus the discount, which
 * only ever bites into the goods. This is the value shown AND the `total_ttc`
 * sent — the server compares it to the cent (`total_mismatch` otherwise).
 */
export function totalAfterRedemption(
  totalCents: number,
  goodsCents: number,
  reward: Valued,
): { redemptionCents: number; finalTotalCents: number } {
  const redemptionCents = redemptionDiscountCents(reward, goodsCents);
  return { redemptionCents, finalTotalCents: Math.max(0, totalCents - redemptionCents) };
}

/** `loyalty/redeem/` reject → customer-facing sentence (page and checkout alike). */
export function redeemErrorMessage(t: TFunction, code: string): string {
  switch (code) {
    case 'insufficient_points': return t('loyalty.errors.insufficient_points');
    case 'out_of_stock':        return t('loyalty.errors.out_of_stock');
    case 'max_uses_reached':    return t('loyalty.errors.max_uses_reached');
    case 'not_found':           return t('loyalty.errors.not_found');
    // SC-1628 — the programme was switched off while the page was open: retrying cannot help.
    case 'disabled':            return t('loyalty.errors.disabled');
    default:                    return t('loyalty.errors.generic');
  }
}

// SC-1081 — jour civil du restaurant, pas du navigateur.
export function formatExpiry(iso: string, tz: string, lng: string): string {
  return new Date(iso).toLocaleDateString(localeTag(lng), {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    timeZone: tz,
  });
}

/**
 * « JJ/MM » a coupon exchanged NOW would expire on — the date the confirm sheet
 * promises the points come back by. Null when the reward has no validity.
 */
export function expiryPreview(
  validityDays: number | null | undefined,
  tz: string,
  lng: string,
  now: Date = new Date(),
): string | null {
  if (!validityDays || validityDays <= 0) return null;
  const end = new Date(now.getTime() + validityDays * 24 * 60 * 60 * 1000);
  return end.toLocaleDateString(localeTag(lng), { day: '2-digit', month: '2-digit', timeZone: tz });
}

/**
 * Points an order of `totalCents` earns: `int(total_ttc × per_euro) + per_visit`,
 * the hub's rule, on the FINAL total (goods after every discount + delivery +
 * options). Integer cents × integer rate, then one division: no float drift
 * (29 × 100 / 100 in floats is 28.999…). Only orders paid ONLINE earn — the
 * caller decides whether to show it.
 */
export function earnEstimate(totalCents: number, perEuro?: number, perVisit?: number): number {
  if (!perEuro && !perVisit) return 0;
  if (totalCents <= 0) return 0;
  return Math.floor((totalCents * (perEuro ?? 0)) / 100) + (perVisit ?? 0);
}

/**
 * A catalog reward and a wallet coupon are the same advantage when name, type
 * and value agree (the coupon carries no reward id). Used to stop offering, at
 * checkout, to spend points on what the customer already owns.
 */
export function sameReward(
  a: { name: string; type: RewardType; value: string | null },
  b: { name: string; type: RewardType; value: string | null },
): boolean {
  if (a.name !== b.name || a.type !== b.type) return false;
  if (a.value == null || b.value == null) return a.value == b.value;
  return Number(a.value) === Number(b.value);
}

/**
 * A reward name is the restaurant's own text — often Latin, dropped inside an
 * Arabic sentence with guillemets around it, where it splits and reorders
 * (m4). Wrapped in a first-strong isolate for RTL languages only; a no-op
 * everywhere else, so left-to-right strings stay byte-identical.
 */
export function isolate(text: string, lng: string): string {
  return lng === 'ar' ? `\u2068${text}\u2069` : text;
}

export function firstNameOf(name: string | null | undefined): string {
  return (name ?? '').trim().split(/\s+/)[0] ?? '';
}

/** The cheapest reward of the catalog, as the public program sends it (SC-1640). */
export type GuestReward = { name: string; points_cost: number };

/**
 * What a guest is told logging in would earn: a number — with what the cheapest
 * reward costs, so « ~20 pts » means something (SC-1640) — or « show your card at
 * the counter ».
 */
export type GuestNudge =
  | { kind: 'points'; points: number; reward: GuestReward | null }
  | { kind: 'counter' };

/** « Dès 20 pts : Dessert offert » — what the cheapest reward costs (SC-1640). */
export function rewardFromLabel(t: TFunction, reward: GuestReward, lng: string): string {
  return t('loyalty.guest.rewardFrom', { n: reward.points_cost, name: isolate(reward.name, lng) });
}

/**
 * The points argument shown to a customer who is NOT logged in (SC-1091): the same
 * rule as for a logged-in one — `int(total × per_euro) + per_visit`, integer cents —
 * but points are credited automatically ONLY for an order paid ONLINE. Pay on site
 * earns nothing by itself: the line then says to show the card at the counter, with
 * no number. `null` (say nothing) when the program is off, unknown (the call
 * failed) or pays nothing.
 */
export function guestNudge(
  program: LoyaltyProgram | undefined,
  totalCents: number,
  channel: 'online' | 'on_site',
): GuestNudge | null {
  if (!program || !program.enabled) return null;
  if (program.points_per_euro <= 0 && program.points_per_visit <= 0) return null;
  if (channel === 'on_site') return { kind: 'counter' };
  const points = earnEstimate(totalCents, program.points_per_euro, program.points_per_visit);
  return points > 0 ? { kind: 'points', points, reward: program.cheapest_reward ?? null } : null;
}
